import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { teamAPI, freelancersAPI, workHoursAPI, timeOffAPI, timeTrackingAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from './ui/dialog';
import {
  Users, Search, Mail, Phone, Building, Loader2, UserPlus, Briefcase,
  Clock, Cake, Palmtree, Plus, Pencil, Trash2, Check, X, ChevronRight,
  ChevronLeft, Timer, ListChecks, Building2, CalendarDays, Coffee, Download, Play,
} from 'lucide-react';
import { toast } from 'sonner';
import { CollaboratorsAdmin } from './CollaboratorsAdmin';
import { InviteMemberButton, SendAccessButton } from './TeamAccessButtons';

const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.08 } } };
const itemVariants = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } };

const roleColors = {
  ceo: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  manager: 'bg-blue-100 text-blue-700 border-blue-200',
  member: 'bg-amber-100 text-amber-700 border-amber-200',
};
const roleLabels = { ceo: 'CEO', manager: 'Manager', member: 'Equipo' };
const MONTHS = ['', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const fmtHM = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
};
const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—');
const dateEs = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const ABSENCE_TYPES = {
  vacaciones: { label: 'Vacaciones', color: 'bg-blue-100 text-blue-700' },
  cita: { label: 'Cita médica', color: 'bg-purple-100 text-purple-700' },
  personal: { label: 'Personal', color: 'bg-amber-100 text-amber-700' },
  otro: { label: 'Otro', color: 'bg-gray-100 text-gray-700' },
  vacation: { label: 'Vacaciones', color: 'bg-blue-100 text-blue-700' },
  no_disponible: { label: 'No disponible', color: 'bg-slate-200 text-slate-700' },
};

function daysUntilBirthday(day, month) {
  if (!day || !month) return null;
  const now = new Date();
  let next = new Date(now.getFullYear(), month - 1, day);
  next.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (next < today) next = new Date(now.getFullYear() + 1, month - 1, day);
  return Math.round((next - today) / (1000 * 60 * 60 * 24));
}

export function TeamPage({ defaultTab } = {}) {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ceo' || user?.role === 'manager';

  const [team, setTeam] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [hoursSummary, setHoursSummary] = useState([]);
  const [hoursEntries, setHoursEntries] = useState([]);
  const [hoursPeriod, setHoursPeriod] = useState('month'); // 'day' | 'week' | 'month'
  const [periodRef, setPeriodRef] = useState(isoDate(new Date()));
  const [hoursView, setHoursView] = useState('members'); // 'members' | 'clients'
  const [activeNow, setActiveNow] = useState([]);
  const [expandedClient, setExpandedClient] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [filterMember, setFilterMember] = useState('__all');
  const [filterClient, setFilterClient] = useState('__all');
  const [globalData, setGlobalData] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [monthly, setMonthly] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(defaultTab || 'directory');
  const isCeoUser = user?.role === 'ceo';

  // Aviso de ausencia (sin aprobación)
  const [absForm, setAbsForm] = useState({ start_date: '', end_date: '', type: 'vacaciones', reason: '' });
  const [savingAbs, setSavingAbs] = useState(false);

  // Directory edit dialog
  const [editMember, setEditMember] = useState(null);
  const [dirForm, setDirForm] = useState({ phone: '', department: '', position: '', birthday_day: '', birthday_month: '', time_tracking_method: 'task' });

  // Detalle de horas de un miembro (ficha)
  const [detailMember, setDetailMember] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Rango del periodo seleccionado (compartido por vista global y ficha de miembro)
  const hoursRange = React.useMemo(() => {
    const d = new Date(periodRef + 'T00:00:00');
    if (hoursPeriod === 'day') return { from: periodRef, to: periodRef };
    if (hoursPeriod === 'week') {
      const dow = (d.getDay() + 6) % 7;
      const monday = new Date(d); monday.setDate(d.getDate() - dow);
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      return { from: isoDate(monday), to: isoDate(sunday) };
    }
    return { from: isoDate(new Date(d.getFullYear(), d.getMonth(), 1)), to: isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
  }, [hoursPeriod, periodRef]);

  const shiftPeriod = (dir) => {
    const d = new Date(periodRef + 'T00:00:00');
    if (hoursPeriod === 'day') d.setDate(d.getDate() + dir);
    else if (hoursPeriod === 'week') d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir, 1);
    setPeriodRef(isoDate(d));
  };

  const periodLabel = React.useMemo(() => {
    const d = new Date(periodRef + 'T00:00:00');
    if (hoursPeriod === 'day') return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    if (hoursPeriod === 'week') return `${dateEs(hoursRange.from)} — ${dateEs(hoursRange.to)}`;
    return d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  }, [hoursPeriod, periodRef, hoursRange]);

  // Freelancer dialog
  const [flDialogOpen, setFlDialogOpen] = useState(false);
  const [editFreelancer, setEditFreelancer] = useState(null);
  const [flForm, setFlForm] = useState({ nombre: '', especialidad: '', contacto: '', tarifa: '', notas: '' });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const { from, to } = hoursRange;
    setLoadingMonthly((prev) => (globalData ? prev : true));
    timeTrackingAPI.getGlobal({
      from, to,
      userId: filterMember !== '__all' ? filterMember : undefined,
      clientId: filterClient !== '__all' ? filterClient : undefined,
    })
      .then((data) => {
        setGlobalData(data);
        if (filterClient === '__all') {
          const seen = {};
          (data.members || []).forEach((m) => (m.by_client || []).forEach((c) => {
            if (c.client_id && c.client_id !== 'none') seen[c.client_id] = c.client_name;
          }));
          setClientOptions(Object.entries(seen).map(([client_id, client_name]) => ({ client_id, client_name })));
        }
      })
      .catch(() => setGlobalData({ members: [] }))
      .finally(() => setLoadingMonthly(false));
  }, [hoursRange, filterMember, filterClient, isAdmin, refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cronómetros activos ahora + auto-refresh cada 30s
  useEffect(() => {
    if (!isAdmin) return;
    const fetchActive = () => timeTrackingAPI.getActiveNow().then((d) => setActiveNow(d.active || [])).catch(() => {});
    fetchActive();
    const iv = setInterval(() => { fetchActive(); setRefreshTick((t) => t + 1); }, 30000);
    return () => clearInterval(iv);
  }, [isAdmin]);

  const activeByUser = React.useMemo(() => {
    const m = {};
    activeNow.forEach((a) => { m[a.user_id] = a; });
    return m;
  }, [activeNow]);

  // Días con registro dentro del detalle de un miembro (método tarea)
  const detailDays = React.useMemo(() => {
    const entries = detailData?.entries || [];
    const map = {};
    entries.forEach((e) => {
      const d = e.date || '—';
      if (!map[d]) map[d] = { date: d, minutes: 0, entries: [] };
      map[d].minutes += e.duration_minutes || 0;
      map[d].entries.push(e);
    });
    return Object.values(map).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [detailData]);

  // Desglose por cliente (agregado de todo el equipo, a partir de la vista global)
  const clientsBreakdown = React.useMemo(() => {
    const map = {};
    (globalData?.members || []).forEach((m) => (m.by_client || []).forEach((c) => {
      const key = c.client_id || 'none';
      if (!map[key]) map[key] = { client_id: key, client_name: c.client_name, minutes: 0, people: [] };
      map[key].minutes += c.minutes;
      map[key].people.push({ user_id: m.user_id, name: m.name, minutes: c.minutes });
    }));
    return Object.values(map)
      .map((c) => ({ ...c, people: c.people.sort((a, b) => b.minutes - a.minutes) }))
      .sort((a, b) => b.minutes - a.minutes);
  }, [globalData]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const data = await timeTrackingAPI.getExport(hoursRange.from, hoursRange.to);
      const rows = data.rows || [];
      if (rows.length === 0) { toast.info('No hay registros en este periodo'); return; }
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = ['Fecha;Miembro;Método;Cliente;Tarea;Minutos;Horas',
        ...rows.map((r) => [r.date, r.member, r.method === 'jornada' ? 'Jornada' : 'Tarea', r.client, r.task, r.minutes, String(r.hours).replace('.', ',')].map(esc).join(';'))].join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registro-tiempo_${hoursRange.from}_${hoursRange.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exportado');
    } catch (e) {
      toast.error(e.message || 'No se pudo exportar');
    } finally {
      setExporting(false);
    }
  };

  const openMemberDetail = async (m) => {
    setDetailMember(m);
    setDetailData(null);
    setLoadingDetail(true);
    try {
      const data = await timeTrackingAPI.getMemberDetail(m.user_id, hoursRange.from, hoursRange.to);
      setDetailData(data);
    } catch (e) {
      toast.error(e.message || 'No se pudo cargar el detalle');
    }
    setLoadingDetail(false);
  };

  const loadData = async () => {
    try {
      const [teamData, flData, toData] = await Promise.all([
        teamAPI.getAll().catch(() => []),
        freelancersAPI.getAll().catch(() => []),
        timeOffAPI.getAll().catch(() => []),
      ]);
      setTeam(teamData);
      setFreelancers(flData);
      setTimeOff(toData);
      const entries = await workHoursAPI.getAll().catch(() => []);
      setHoursEntries(entries);
      if (isAdmin) {
        const summary = await workHoursAPI.getSummary().catch(() => []);
        setHoursSummary(summary);
      }
    } catch (e) {
      console.error('Error loading team:', e);
    } finally {
      setLoading(false);
    }
  };

  const memberName = (uid) => team.find((m) => m.id === uid)?.name || '—';

  // ----- Directory edit -----
  const openEdit = (m) => {
    setEditMember(m);
    setDirForm({
      phone: m.profile?.phone || '',
      department: m.profile?.department || '',
      position: m.profile?.position || '',
      birthday_day: m.profile?.birthday_day || '',
      birthday_month: m.profile?.birthday_month || '',
      time_tracking_method: m.time_tracking_method || 'task',
    });
  };

  const saveDirectory = async () => {
    try {
      const payload = {
        phone: dirForm.phone || undefined,
        department: dirForm.department || undefined,
        position: dirForm.position || undefined,
        birthday_day: dirForm.birthday_day ? parseInt(dirForm.birthday_day, 10) : undefined,
        birthday_month: dirForm.birthday_month ? parseInt(dirForm.birthday_month, 10) : undefined,
      };
      await teamAPI.updateDirectory(editMember.id, payload);
      if (isAdmin && dirForm.time_tracking_method !== (editMember.time_tracking_method || 'task')) {
        await teamAPI.setTimeMethod(editMember.id, dirForm.time_tracking_method);
      }
      toast.success('Ficha actualizada');
      setEditMember(null);
      loadData();
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar');
    }
  };

  // ----- Freelancers -----
  const openFreelancer = (fl = null) => {
    setEditFreelancer(fl);
    setFlForm(fl ? { nombre: fl.nombre || '', especialidad: fl.especialidad || '', contacto: fl.contacto || '', tarifa: fl.tarifa || '', notas: fl.notas || '' }
                  : { nombre: '', especialidad: '', contacto: '', tarifa: '', notas: '' });
    setFlDialogOpen(true);
  };

  const saveFreelancer = async () => {
    if (!flForm.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      if (editFreelancer) await freelancersAPI.update(editFreelancer.id, flForm);
      else await freelancersAPI.create(flForm);
      toast.success('Freelancer guardado');
      setFlDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar');
    }
  };

  const deleteFreelancer = async (id) => {
    try { await freelancersAPI.delete(id); toast.success('Freelancer eliminado'); loadData(); }
    catch (e) { toast.error(e.message || 'No se pudo eliminar'); }
  };

  // ----- Ausencias -----
  const submitAbsence = async () => {
    if (!absForm.start_date || !absForm.end_date) { toast.error('Indica fecha de inicio y fin'); return; }
    if (absForm.end_date < absForm.start_date) { toast.error('La fecha fin no puede ser anterior al inicio'); return; }
    setSavingAbs(true);
    try {
      await timeOffAPI.request(absForm);
      toast.success('Ausencia registrada');
      setAbsForm({ start_date: '', end_date: '', type: 'vacaciones', reason: '' });
      loadData();
    } catch (e) { toast.error(e.message || 'No se pudo registrar'); }
    setSavingAbs(false);
  };
  const deleteAbsence = async (id) => {
    if (!window.confirm('¿Eliminar esta ausencia?')) return;
    try { await timeOffAPI.remove(id); toast.success('Ausencia eliminada'); loadData(); }
    catch (e) { toast.error(e.message || 'No se pudo eliminar'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const filteredTeam = team.filter((m) =>
    m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.profile?.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const birthdays = team
    .filter((m) => m.profile?.birthday_day && m.profile?.birthday_month)
    .map((m) => ({ ...m, dias: daysUntilBirthday(m.profile.birthday_day, m.profile.birthday_month) }))
    .sort((a, b) => a.dias - b.dias);

  // Miembros: solo "Ausencias" (su propia página), sin pestañas de Equipo (Directorio/Horas/Freelancers/Cumpleaños son de CEO)
  const memberOnlyAbsences = user?.role === 'member';
  const effectiveTab = memberOnlyAbsences ? 'vacaciones' : activeTab;

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6" data-testid="team-page">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{memberOnlyAbsences ? 'Ausencias' : 'Equipo'}</h1>
          <p className="text-muted-foreground mt-1">{memberOnlyAbsences ? 'Avisa de tus días fuera (Out of office) y consulta las ausencias del equipo' : 'Directorio y gestión del equipo de Karma Digital'}</p>
        </div>
        {!memberOnlyAbsences && (
          <Badge variant="outline" className="text-lg px-4 py-2">
            <Users className="w-4 h-4 mr-2" />{team.length} miembros
          </Badge>
        )}
      </motion.div>

      <Tabs value={effectiveTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className={`grid grid-cols-3 md:grid-cols-6 w-full ${memberOnlyAbsences ? 'hidden' : ''}`}>
          <TabsTrigger value="directory" data-testid="tab-directory">Directorio</TabsTrigger>
          <TabsTrigger value="horas" data-testid="tab-horas">Horas</TabsTrigger>
          <TabsTrigger value="vacaciones" data-testid="tab-vacaciones">Vacaciones</TabsTrigger>
          <TabsTrigger value="freelancers" data-testid="tab-freelancers">Freelancers</TabsTrigger>
          <TabsTrigger value="cumpleanos" data-testid="tab-cumpleanos">Cumpleaños</TabsTrigger>
          {isCeoUser && <TabsTrigger value="colaboradores" data-testid="tab-colaboradores">Colaboradores</TabsTrigger>}
        </TabsList>

        {/* DIRECTORIO */}
        <TabsContent value="directory" className="space-y-6">
          <motion.div initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }}>
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre, email o área..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-team-input" />
            </div>
            {user?.role === 'ceo' && <div className="mt-3"><InviteMemberButton onDone={() => window.location.reload()} /></div>}
          </motion.div>
          <motion.div initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTeam.map((m) => {
                const canEdit = isAdmin || m.id === user?.id;
                return (
                  <Card key={m.id} className="card-hover" data-testid={`team-member-${m.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={m.avatar} />
                          <AvatarFallback className="bg-primary/10 text-primary text-lg">{m.name?.charAt(0) || 'U'}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-semibold text-lg truncate">{m.name}</h3>
                            <Badge className={roleColors[m.role]}>{roleLabels[m.role]}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{m.profile?.position || 'Sin puesto'}</p>
                          {m.profile?.department && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><Building className="w-3 h-3" />{m.profile.department}</p>
                          )}
                          <div className="mt-3 pt-3 border-t space-y-1.5">
                            <a href={`mailto:${m.email}`} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1.5 truncate"><Mail className="w-4 h-4" />{m.email}</a>
                            {m.profile?.phone && (
                              <a href={`tel:${m.profile.phone}`} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1.5"><Phone className="w-4 h-4" />{m.profile.phone}</a>
                            )}
                          </div>
                          {canEdit && (
                            <Button variant="ghost" size="sm" className="mt-2 -ml-2 h-8 text-primary" onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Editar ficha
                            </Button>
                          )}
                          {user?.role === 'ceo' && m.id !== user?.id && <SendAccessButton member={m} />}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {filteredTeam.length === 0 && (
              <div className="text-center py-12"><Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" /><p className="text-muted-foreground">No se encontraron miembros</p></div>
            )}
          </motion.div>
        </TabsContent>

        {/* HORAS */}
        <TabsContent value="horas" className="space-y-6">
          <motion.div initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-semibold flex items-center gap-2"><Clock className="w-5 h-5 text-primary" />Horas del equipo</h2>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg border p-0.5 bg-muted/40">
                  <button onClick={() => setHoursPeriod('day')} className={`text-sm px-3 py-1.5 rounded-md ${hoursPeriod === 'day' ? 'bg-background shadow font-medium' : 'text-muted-foreground'}`} data-testid="period-day-btn">Día</button>
                  <button onClick={() => setHoursPeriod('week')} className={`text-sm px-3 py-1.5 rounded-md ${hoursPeriod === 'week' ? 'bg-background shadow font-medium' : 'text-muted-foreground'}`} data-testid="period-week-btn">Semana</button>
                  <button onClick={() => setHoursPeriod('month')} className={`text-sm px-3 py-1.5 rounded-md ${hoursPeriod === 'month' ? 'bg-background shadow font-medium' : 'text-muted-foreground'}`} data-testid="period-month-btn">Mes</button>
                </div>
                <div className="inline-flex items-center gap-1 border rounded-lg bg-background px-1 py-0.5">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftPeriod(-1)} data-testid="period-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-sm font-medium capitalize min-w-[140px] text-center" data-testid="period-label">{periodLabel}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftPeriod(1)} data-testid="period-next-btn"><ChevronRight className="w-4 h-4" /></Button>
                </div>
                <Button variant="outline" size="sm" onClick={() => setPeriodRef(isoDate(new Date()))} data-testid="period-today-btn">Hoy</Button>
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting} data-testid="export-csv-btn">
                    {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} CSV
                  </Button>
                )}
              </div>
            </div>

            {!isAdmin ? (
              <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">Consulta tus horas en <strong>Registro de Tiempo</strong>.</CardContent></Card>
            ) : (
              <>
                {/* Quién está trabajando ahora */}
                {activeNow.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="active-now-bar">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ahora mismo:</span>
                    {activeNow.map((a) => (
                      <Badge key={`${a.user_id}-${a.type}`} variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700" data-testid={`active-now-${a.user_id}`}>
                        <span className={`w-2 h-2 rounded-full mr-1.5 ${a.status === 'paused' ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} />
                        {a.name} · {a.type === 'jornada' ? (a.status === 'paused' ? 'jornada en pausa' : 'jornada activa') : (a.task_title || 'tarea')}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Sub-vista: Por miembro / Por cliente */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="inline-flex rounded-lg border p-0.5 bg-muted/40">
                    <button onClick={() => setHoursView('members')} className={`text-sm px-3 py-1.5 rounded-md ${hoursView === 'members' ? 'bg-background shadow font-medium' : 'text-muted-foreground'}`} data-testid="view-members-btn">Por miembro</button>
                    <button onClick={() => setHoursView('clients')} className={`text-sm px-3 py-1.5 rounded-md ${hoursView === 'clients' ? 'bg-background shadow font-medium' : 'text-muted-foreground'}`} data-testid="view-clients-btn">Por cliente</button>
                  </div>
                  {hoursView === 'members' && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Miembro</span>
                        <select value={filterMember} onChange={(e) => setFilterMember(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-background" data-testid="filter-member">
                          <option value="__all">Todos</option>
                          {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Cliente</span>
                        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-background" data-testid="filter-client">
                          <option value="__all">Todos</option>
                          {clientOptions.map((c) => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  {globalData && (
                    <Badge variant="outline" className="ml-auto text-sm" data-testid="global-total">
                      Total periodo: {globalData.total_hours ?? 0}h
                    </Badge>
                  )}
                </div>

                {loadingMonthly ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : hoursView === 'members' ? (
                  <Card>
                    <CardContent className="p-0 overflow-x-auto">
                      <table className="w-full text-sm" data-testid="hours-members-table">
                        <thead>
                          <tr className="border-b text-left text-xs text-muted-foreground uppercase tracking-wide">
                            <th className="px-4 py-3 font-medium">Miembro</th>
                            <th className="px-3 py-3 font-medium">Método</th>
                            <th className="px-3 py-3 font-medium">Ahora</th>
                            <th className="px-3 py-3 font-medium text-right">Total periodo</th>
                            <th className="px-3 py-3 w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(globalData?.members || []).map((m) => {
                            const act = activeByUser[m.user_id];
                            return (
                              <tr key={m.user_id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer transition-colors" onClick={() => openMemberDetail(m)} data-testid={`hours-row-${m.user_id}`}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium">{m.name}</span>
                                    <Badge className={roleColors[m.role]}>{roleLabels[m.role]}</Badge>
                                  </div>
                                </td>
                                <td className="px-3 py-3">
                                  <Badge variant="outline" className="text-[10px]">
                                    {m.method === 'jornada' ? <Timer className="w-3 h-3 mr-1" /> : <ListChecks className="w-3 h-3 mr-1" />}
                                    {m.method === 'jornada' ? 'Jornada' : 'Por tarea'}
                                  </Badge>
                                </td>
                                <td className="px-3 py-3">
                                  {act ? (
                                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium" data-testid={`hours-active-${m.user_id}`}>
                                      <span className={`w-2 h-2 rounded-full ${act.status === 'paused' ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} />
                                      <Play className="w-3 h-3" />
                                      <span className="truncate max-w-[180px]">{act.type === 'jornada' ? (act.status === 'paused' ? 'En pausa' : 'Jornada') : (act.task_title || 'Tarea')}</span>
                                    </span>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-3 text-right">
                                  <span className="font-bold text-primary" data-testid={`hours-total-${m.user_id}`}>{fmtHM(m.total_minutes)}</span>
                                </td>
                                <td className="px-3 py-3"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                              </tr>
                            );
                          })}
                          {(globalData?.members || []).length === 0 && (
                            <tr><td colSpan={5} className="py-8 text-center text-muted-foreground text-sm">No hay datos para el periodo/filtros seleccionados.</td></tr>
                          )}
                        </tbody>
                        {(globalData?.members || []).length > 0 && (
                          <tfoot>
                            <tr className="border-t bg-muted/30">
                              <td className="px-4 py-3 font-semibold" colSpan={3}>Total equipo</td>
                              <td className="px-3 py-3 text-right font-bold text-primary" data-testid="hours-grand-total">{fmtHM(globalData?.total_minutes)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <div className="divide-y" data-testid="hours-clients-list">
                        {clientsBreakdown.length === 0 && (
                          <p className="py-8 text-center text-muted-foreground text-sm">Sin tiempo asignado a clientes en este periodo.</p>
                        )}
                        {clientsBreakdown.map((c) => (
                          <div key={c.client_id} data-testid={`client-hours-${c.client_id}`}>
                            <button
                              className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                              onClick={() => setExpandedClient(expandedClient === c.client_id ? null : c.client_id)}
                              data-testid={`client-hours-toggle-${c.client_id}`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Building2 className="w-4 h-4 text-primary shrink-0" />
                                <span className="font-medium truncate">{c.client_name}</span>
                                <Badge variant="outline" className="text-[10px] shrink-0">{c.people.length} {c.people.length === 1 ? 'persona' : 'personas'}</Badge>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="font-bold text-primary">{fmtHM(c.minutes)}</span>
                                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedClient === c.client_id ? 'rotate-90' : ''}`} />
                              </div>
                            </button>
                            {expandedClient === c.client_id && (
                              <div className="px-4 pb-3 space-y-1.5 bg-muted/20">
                                {c.people.map((p) => (
                                  <div key={p.user_id} className="flex items-center justify-between text-sm pl-6 py-1" data-testid={`client-person-${c.client_id}-${p.user_id}`}>
                                    <span className="text-muted-foreground">{p.name}</span>
                                    <span className="font-medium">{fmtHM(p.minutes)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {clientsBreakdown.length > 0 && (
                        <p className="px-4 py-2.5 text-[11px] text-muted-foreground border-t">
                          Nota: los miembros con método «jornada» fichan el día completo sin repartir por cliente, por lo que sus horas no aparecen aquí.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </motion.div>
        </TabsContent>

        {/* VACACIONES / AUSENCIAS */}
        <TabsContent value="vacaciones" className="space-y-6">
          <motion.div initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Marcar ausencia (todos: CEOs y miembros) */}
            <Card data-testid="absence-form-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Palmtree className="w-5 h-5 text-primary" /> Avisar de una ausencia</CardTitle>
                <p className="text-sm text-muted-foreground">Informa de los días que estarás fuera (no hay que pedir permiso). El equipo lo verá al planificar.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                  <div className="space-y-1.5"><Label>Desde</Label><Input type="date" value={absForm.start_date} onChange={(e) => setAbsForm({ ...absForm, start_date: e.target.value })} data-testid="absence-start-input" /></div>
                  <div className="space-y-1.5"><Label>Hasta</Label><Input type="date" value={absForm.end_date} onChange={(e) => setAbsForm({ ...absForm, end_date: e.target.value })} data-testid="absence-end-input" /></div>
                  <div className="space-y-1.5">
                    <Label>Tipo</Label>
                    <select value={absForm.type} onChange={(e) => setAbsForm({ ...absForm, type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm bg-background h-10" data-testid="absence-type-select">
                      {Object.entries(ABSENCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <Button onClick={submitAbsence} disabled={savingAbs} className="h-10" data-testid="submit-absence-btn"><Plus className="w-4 h-4 mr-1" /> Marcar ausencia</Button>
                </div>
                <div className="space-y-1.5"><Label>Nota (opcional)</Label><Input value={absForm.reason} onChange={(e) => setAbsForm({ ...absForm, reason: e.target.value })} placeholder="Ej: viaje, revisión médica..." data-testid="absence-note-input" /></div>
              </CardContent>
            </Card>

            {/* Todas las ausencias del equipo */}
            <Card>
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Ausencias del equipo</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {timeOff.map((t) => {
                  const cfg = ABSENCE_TYPES[t.type] || ABSENCE_TYPES.otro;
                  const canDelete = t.user_id === user?.id || isAdmin;
                  return (
                    <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 rounded-lg border hover:bg-muted/30" data-testid={`absence-row-${t.id}`}>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                        <span className="font-medium w-40">{t.user_name || memberName(t.user_id)}</span>
                        <span className="text-muted-foreground">{t.start_date ? new Date(t.start_date + 'T00:00:00').toLocaleDateString('es-ES') : '—'} → {t.end_date ? new Date(t.end_date + 'T00:00:00').toLocaleDateString('es-ES') : '—'}</span>
                        <Badge className={cfg.color}>{cfg.label}</Badge>
                        {t.reason && <span className="text-muted-foreground italic">{t.reason}</span>}
                      </div>
                      {canDelete && (
                        <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={() => deleteAbsence(t.id)} data-testid={`delete-absence-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  );
                })}
                {timeOff.length === 0 && <p className="py-8 text-center text-muted-foreground text-sm">No hay ausencias registradas. Marca la primera arriba.</p>}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* FREELANCERS */}
        <TabsContent value="freelancers" className="space-y-6">
          <motion.div initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Cartera de Freelancers</h2>
              <Button className="gradient-primary text-white" onClick={() => openFreelancer()} data-testid="add-freelancer-btn">
                <UserPlus className="w-4 h-4 mr-2" />Añadir Freelancer
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {freelancers.map((fl) => (
                <Card key={fl.id} className="card-hover" data-testid={`freelancer-${fl.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-12 h-12"><AvatarFallback className="bg-primary/10 text-primary">{fl.nombre?.charAt(0) || 'F'}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-semibold truncate">{fl.nombre}</h3>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openFreelancer(fl)} data-testid={`edit-freelancer-${fl.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => deleteFreelancer(fl.id)} data-testid={`delete-freelancer-${fl.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                        {fl.especialidad && <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><Briefcase className="w-3 h-3" />{fl.especialidad}</p>}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                          {fl.contacto && <span className="text-muted-foreground">{fl.contacto}</span>}
                          {fl.tarifa && <span className="font-medium">{fl.tarifa}</span>}
                        </div>
                        {fl.notas && <p className="text-xs text-muted-foreground mt-2">{fl.notas}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {freelancers.length === 0 && (
              <div className="text-center py-12"><Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" /><p className="text-muted-foreground">Aún no hay freelancers. Añade el primero.</p></div>
            )}
          </motion.div>
        </TabsContent>

        {/* CUMPLEAÑOS */}
        <TabsContent value="cumpleanos" className="space-y-6">
          <motion.div initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }}>
            <h2 className="text-xl font-semibold mb-4">Próximos Cumpleaños</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {birthdays.map((m) => (
                <Card key={m.id} className="card-hover" data-testid={`birthday-${m.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0"><Cake className="w-6 h-6 text-pink-600" /></div>
                      <div>
                        <h3 className="font-semibold">{m.name}</h3>
                        <p className="text-sm text-muted-foreground">{m.profile.birthday_day} de {MONTHS[m.profile.birthday_month]}</p>
                        <Badge className="mt-1 bg-pink-100 text-pink-700">{m.dias === 0 ? '¡Hoy!' : `En ${m.dias} días`}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {birthdays.length === 0 && (
              <div className="text-center py-12"><Cake className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" /><p className="text-muted-foreground">Nadie ha configurado su cumpleaños todavía (desde <strong>Mi Perfil</strong>).</p></div>
            )}
          </motion.div>
        </TabsContent>

        {isCeoUser && (
          <TabsContent value="colaboradores" className="space-y-6">
            <CollaboratorsAdmin />
          </TabsContent>
        )}
      </Tabs>

      {/* Edit directory dialog */}
      <Dialog open={!!editMember} onOpenChange={(o) => !o && setEditMember(null)}>
        <DialogContent data-testid="edit-directory-dialog">
          <DialogHeader><DialogTitle>Editar ficha · {editMember?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Teléfono</Label><Input value={dirForm.phone} onChange={(e) => setDirForm({ ...dirForm, phone: e.target.value })} placeholder="+34 600 000 000" data-testid="dir-phone-input" /></div>
            <div className="space-y-2"><Label>Área / Departamento</Label><Input value={dirForm.department} onChange={(e) => setDirForm({ ...dirForm, department: e.target.value })} placeholder="Contenido, Ads..." data-testid="dir-department-input" /></div>
            <div className="space-y-2"><Label>Puesto</Label><Input value={dirForm.position} onChange={(e) => setDirForm({ ...dirForm, position: e.target.value })} placeholder="Copywriter, Diseñador..." data-testid="dir-position-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Cumpleaños (día)</Label><Input type="number" min={1} max={31} value={dirForm.birthday_day} onChange={(e) => setDirForm({ ...dirForm, birthday_day: e.target.value })} data-testid="dir-bday-day-input" /></div>
              <div className="space-y-2"><Label>Cumpleaños (mes)</Label><Input type="number" min={1} max={12} value={dirForm.birthday_month} onChange={(e) => setDirForm({ ...dirForm, birthday_month: e.target.value })} data-testid="dir-bday-month-input" /></div>
            </div>
            {isAdmin && (
              <div className="space-y-2 pt-2 border-t">
                <Label className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Método de registro de tiempo</Label>
                <select
                  value={dirForm.time_tracking_method}
                  onChange={(e) => setDirForm({ ...dirForm, time_tracking_method: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
                  data-testid="dir-time-method-select"
                >
                  <option value="task">Por tarea (cronómetro en cada tarea)</option>
                  <option value="jornada">Por jornada (fichar entrada/salida)</option>
                </select>
                <p className="text-[11px] text-muted-foreground">Define cómo registra su tiempo este miembro. Solo lo cambian los CEOs.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMember(null)}>Cancelar</Button>
            <Button onClick={saveDirectory} data-testid="save-directory-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Freelancer dialog */}
      <Dialog open={flDialogOpen} onOpenChange={setFlDialogOpen}>
        <DialogContent data-testid="freelancer-dialog">
          <DialogHeader><DialogTitle>{editFreelancer ? 'Editar freelancer' : 'Nuevo freelancer'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre *</Label><Input value={flForm.nombre} onChange={(e) => setFlForm({ ...flForm, nombre: e.target.value })} data-testid="fl-nombre-input" /></div>
            <div className="space-y-2"><Label>Especialidad</Label><Input value={flForm.especialidad} onChange={(e) => setFlForm({ ...flForm, especialidad: e.target.value })} placeholder="Diseño, Video, Copy..." data-testid="fl-especialidad-input" /></div>
            <div className="space-y-2"><Label>Contacto</Label><Input value={flForm.contacto} onChange={(e) => setFlForm({ ...flForm, contacto: e.target.value })} placeholder="email / teléfono" data-testid="fl-contacto-input" /></div>
            <div className="space-y-2"><Label>Tarifa</Label><Input value={flForm.tarifa} onChange={(e) => setFlForm({ ...flForm, tarifa: e.target.value })} placeholder="35€/h, 500€/proyecto..." data-testid="fl-tarifa-input" /></div>
            <div className="space-y-2"><Label>Notas</Label><Textarea value={flForm.notas} onChange={(e) => setFlForm({ ...flForm, notas: e.target.value })} rows={2} data-testid="fl-notas-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveFreelancer} data-testid="save-freelancer-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalle de horas de un miembro */}
      <Dialog open={!!detailMember} onOpenChange={(o) => !o && setDetailMember(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="member-hours-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Clock className="w-5 h-5 text-primary" /> Horas · {detailMember?.name}
              {detailMember && <Badge variant="outline" className="text-[10px]">{detailMember.method === 'jornada' ? 'Por jornada' : 'Por tarea'}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : detailData ? (
            <div className="space-y-4">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-primary" data-testid="member-detail-total">{detailData.range_total_hours ?? 0}h</span>
                <span className="text-sm text-muted-foreground">del {dateEs(detailData.from)} al {dateEs(detailData.to)}</span>
              </div>

              {detailData.method === 'jornada' ? (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Jornadas fichadas</p>
                  <div className="divide-y rounded-lg border">
                    {(detailData.sessions || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Sin jornadas en este periodo.</p>}
                    {(detailData.sessions || []).map((s) => (
                      <div key={s.id} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm" data-testid={`detail-session-${s.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-medium w-28 shrink-0 capitalize">{dateEs(s.date)}</span>
                          <span className="text-muted-foreground">{timeOf(s.started_at)} → {s.ended_at ? timeOf(s.ended_at) : 'en curso'}</span>
                          {s.paused_minutes > 0 && <Badge variant="outline" className="text-[10px]"><Coffee className="w-3 h-3 mr-1" />{fmtHM(s.paused_minutes)}</Badge>}
                        </div>
                        <span className="font-semibold text-primary shrink-0">{fmtHM(s.worked_minutes)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {(detailData.by_client?.length > 0) && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Por cliente</p>
                      <div className="space-y-1.5">
                        {detailData.by_client.map((c) => (
                          <div key={c.client_id} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground truncate">{c.client_name}</span>
                            <span className="font-medium">{fmtHM(c.minutes)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(detailData.by_task?.length > 0) && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" /> Por tarea</p>
                      <div className="divide-y rounded-lg border">
                        {detailData.by_task.map((tk, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`detail-task-${i}`}>
                            <span className="truncate">{tk.task_title}</span>
                            <span className="font-semibold shrink-0">{fmtHM(tk.minutes)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(detailDays.length > 0) && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Días con registro</p>
                      <div className="divide-y rounded-lg border">
                        {detailDays.map((d) => (
                          <div key={d.date} className="px-3 py-2" data-testid={`detail-day-${d.date}`}>
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium capitalize">{dateEs(d.date)}</span>
                              <span className="font-semibold text-primary">{fmtHM(d.minutes)}</span>
                            </div>
                            <div className="mt-1 space-y-0.5">
                              {d.entries.map((e) => (
                                <div key={e.id} className="flex items-center justify-between gap-2 text-xs text-muted-foreground pl-2">
                                  <span className="truncate">{e.task_title || 'Tarea'}{e.client_name ? ` · ${e.client_name}` : ''}</span>
                                  <span className="shrink-0">{fmtHM(e.duration_minutes)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(!detailData.entries || detailData.entries.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-6">Sin tiempo registrado en este periodo.</p>
                  )}
                </>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
